import React, { createContext, useCallback, useContext, useMemo } from 'react';

import invariant from 'tiny-invariant';

import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';

import type {
	CollapseCallback,
	ExpandCallback,
	ResizeEndCallback,
	PeekStartCallback,
	PeekEndCallback,
	ResizeStartCallback,
	SideNavVisibilityContextType,
	SideNavVisibilityProviderProps,
} from './types';

export const SideNavVisibilityContext: React.Context<SideNavVisibilityContextType | null> =
	createContext<SideNavVisibilityContextType | null>(null);

/*
 * useSideNavVisibility - Returns Visibility callbacks from Navigation System
 */
export const useSideNavVisibility = (): SideNavVisibilityContextType => {
	const context = useContext(SideNavVisibilityContext);
	invariant(context, 'useSideNavVisibility must be used within an SideNavVisibilityProvider');

	return context;
};

/*
 *  SideNavVisibilityProvider - Wrapper for the Navigation System visibility callbacks.
 */
export function SideNavVisibilityProvider({
	children,
	onCollapse,
	onExpand,
	onPeekStart,
	onPeekEnd,
	onResizeEnd,
	onResizeStart,
	setUserSideNavData,
}: SideNavVisibilityProviderProps): React.JSX.Element {
	const { fireEvent } = useAnalyticsEvents();

	const onCollapseHandler = useCallback<CollapseCallback>(
		(args) => {
			fireEvent('ui.sideNav.collapsed', { ...args });
			setUserSideNavData({ defaultCollapsed: true });
			onCollapse?.(args);
		},
		[fireEvent, onCollapse, setUserSideNavData],
	);

	const onExpandHandler = useCallback<ExpandCallback>(
		(args) => {
			fireEvent('ui.sideNav.expanded', { ...args });
			setUserSideNavData({ defaultCollapsed: false });
			onExpand?.(args);
		},
		[fireEvent, onExpand, setUserSideNavData],
	);

	const onPeekStartHandler = useCallback<PeekStartCallback>(() => {
		onPeekStart?.();
	}, [onPeekStart]);

	const onPeekEndHandler = useCallback<PeekEndCallback>(
		(args) => {
			onPeekEnd?.(args);
		},
		[onPeekEnd],
	);

	const onResizeStartHandler = useCallback<ResizeStartCallback>(
		(args) => {
			fireEvent('ui.sideNav.resizeStart', { ...args });
			onResizeStart?.(args);
		},
		[fireEvent, onResizeStart],
	);

	const onResizeEndHandler = useCallback<ResizeEndCallback>(
		(args) => {
			fireEvent('ui.sideNav.resizeEnd', { ...args });
			setUserSideNavData({ defaultWidth: args.finalWidth });
			onResizeEnd?.(args);
		},
		[fireEvent, onResizeEnd, setUserSideNavData],
	);

	const stableContextValue = useMemo(
		() => ({
			onExpand: onExpandHandler,
			onCollapse: onCollapseHandler,
			onPeekStart: onPeekStartHandler,
			onPeekEnd: onPeekEndHandler,
			onResizeEnd: onResizeEndHandler,
			onResizeStart: onResizeStartHandler,
		}),
		[
			onExpandHandler,
			onCollapseHandler,
			onResizeEndHandler,
			onResizeStartHandler,
			onPeekStartHandler,
			onPeekEndHandler,
		],
	);

	return (
		<SideNavVisibilityContext.Provider value={stableContextValue}>
			{children}
		</SideNavVisibilityContext.Provider>
	);
}
